import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {Place} from "../../admin/artisticobjects/types/Place";
import {Image} from "../../admin/artisticobjects/types/Image";
import {ArtisticObjectListing} from "../../admin/artisticobjects/types/ArtisticObject";
import {handleError} from "../../common/utils/errorHandling";
import placesService from "../../admin/artisticobjects/services/placesService";
import searchService from "../services/searchService";
import {ImageType} from "../../admin/artisticobjects/types/ImageType";
import {formatAddress} from "../../common/utils/addressUtils";
import DetailsLayout from "../../common/components/details/DetailsLayout";
import DetailsImageSection from "../../common/components/details/DetailsImageSection";
import DetailsInfoCard from "../../common/components/details/DetailsInfoCard";
import DetailsPlanSection from "../../common/components/details/DetailsPlanSection";
import DetailsArtisticObjectsList from "../../common/components/details/DetailsArtisticObjectsList";
import DetailsDescriptionSection from "../../common/components/DetailsDescriptionSection";
import DetailsMapSection from "../../common/components/details/DetailsMapSection";
import archivesCacheService from "../services/archivesCacheService";
import bibliographyItemsCacheService from "../services/bibliographyItemsCacheService";
import {AbbreviationMap} from "../../common/utils/abbreviationProcessor";
import Breadcrumbs, {BreadcrumbItem} from "../../common/components/Breadcrumbs";
import organizationalUnitsService from "../../admin/artisticobjects/services/organizationalUnitsService";
import {HierarchicalOrganizationalUnit} from "../../admin/artisticobjects/types/OrganizationalUnit";
import {parseFootnotes} from "../../common/utils/footnoteParser";
import './ArtisticObjectDetails.scss';
import {PlaceSections} from "../../admin/core/types/DictionaryItem";
import {Section} from "../../admin/artisticobjects/types/Section";
import {useAuth} from "../../common/contexts/AuthContext";
import {UserRole} from "../../admin/users/types/User";
import {getValueWithFallback} from "../../common/types/StringTranslations";
import {DEFAULT_LANGUAGE} from "../../common/i18n/config";

const sortPlaceSections = (sections: Section[]): Section[] => {
  return sections.sort((a, b) => {
    const order = Object.values(PlaceSections);
    const indexA = order.indexOf(a.type.alias as PlaceSections);
    const indexB = order.indexOf(b.type.alias as PlaceSections);
    return indexA - indexB;
  });
};

const filterSectionsByLanguage = (sections: Section[], currentLanguage: string): Section[] => {
  const nonEmptySections = sections.filter(section => section.content?.trim());
  const sectionsByType = new Map<string, Section[]>();

  nonEmptySections.forEach(section => {
    const typeAlias = section.type.alias;
    if (!sectionsByType.has(typeAlias)) {
      sectionsByType.set(typeAlias, []);
    }
    sectionsByType.get(typeAlias)!.push(section);
  });

  const filteredSections: Section[] = [];

  sectionsByType.forEach((sectionsOfType) => {
    const currentLangSection = sectionsOfType.find(s => s.language === currentLanguage);
    const defaultLangSection = sectionsOfType.find(s => s.language === DEFAULT_LANGUAGE);
    const firstSection = sectionsOfType[0];

    const selectedSection = currentLangSection || defaultLangSection || firstSection;
    if (selectedSection) {
      filteredSections.push(selectedSection);
    }
  });

  return filteredSections;
};

const PlaceDetails: React.FC = () => {
  const {t, i18n} = useTranslation();
  const {id} = useParams<{ id: string }>();
  const [place, setPlace] = useState<Place | undefined>();
  const [image, setImage] = useState<Image | undefined>();
  const [artisticObjects, setArtisticObjects] = useState<ArtisticObjectListing[]>([]);
  const [archiveAbbreviations, setArchiveAbbreviations] = useState<AbbreviationMap>({});
  const [bibliographyAbbreviations, setBibliographyAbbreviations] = useState<AbbreviationMap>({});
  const [footnoteReferences, setFootnoteReferences] = useState<AbbreviationMap>({});
  const [hierarchicalUnit, setHierarchicalUnit] = useState<HierarchicalOrganizationalUnit | undefined>();
  const navigate = useNavigate();
  const {user} = useAuth();

  const placeName = place ? getValueWithFallback(place.name, i18n.language) : '';

  useEffect(() => {
    if (id) {
      fetchPlace(id);
      fetchArtisticObjects(id);
    }
  }, [id]);

  useEffect(() => {
    if (place?.publication?.id) {
      loadArchives(place.publication.id);
      loadBibliographyItems(place.publication.id);
    }
  }, [place?.publication?.id]);

  useEffect(() => {
    if (place?.churchUnit?.id) {
      fetchHierarchicalUnit(place.churchUnit.id);
    }
  }, [place?.churchUnit?.id]);

  useEffect(() => {
    if (place) {
      extractFootnotes(place);
    }
  }, [place, i18n.language]);

  const fetchPlace = async (placeId: string): Promise<void> => {
    await handleError(
      async () => {
        const placeData = await placesService.getPlace(placeId);
        setPlace(placeData);
        const firstNonPlanImage = placeData?.images?.find(img => img.imageType !== ImageType.PLAN);
        setImage(firstNonPlanImage);
      },
      t('common.errors.fetchingPlace')
    );
  };

  const extractFootnotes = (place: Place): void => {
    if (!place.sections) {
      setFootnoteReferences({});
      return;
    }

    const footnotesSections = place.sections.filter(
      section => section.type.alias === 'FTNT' || section.type.alias === 'PPLK'
    );

    if (footnotesSections.length === 0) {
      setFootnoteReferences({});
      return;
    }

    const currentLangSection = footnotesSections.find(s => s.language === i18n.language);
    const defaultLangSection = footnotesSections.find(s => s.language === DEFAULT_LANGUAGE);
    const footnotesSection = currentLangSection || defaultLangSection || footnotesSections[0];

    const footnotes = parseFootnotes(footnotesSection.content);
    setFootnoteReferences(footnotes);
  };

  const fetchArtisticObjects = async (placeId: string): Promise<void> => {
    await handleError(
      async () => {
        const objectsData = await searchService.getArtisticObjectsByPlaceId(placeId);
        setArtisticObjects(objectsData);
      },
      t('common.errors.searchingArtisticObjects')
    );
  };

  const loadArchives = async (publicationId: string): Promise<void> => {
    await handleError(
      async () => {
        const archives = await archivesCacheService.getArchivesForPublication(publicationId);
        const abbrMap: AbbreviationMap = {};
        archives.forEach(archive => {
          abbrMap[archive.abbreviation] = archive.name;
        });
        setArchiveAbbreviations(abbrMap);
      },
      t('common.errors.loadingArchives')
    );
  };

  const loadBibliographyItems = async (publicationId: string): Promise<void> => {
    await handleError(
      async () => {
        const items = await bibliographyItemsCacheService.getBibliographyItemsForPublication(publicationId);
        const abbrMap: AbbreviationMap = {};
        items.forEach(item => {
          abbrMap[item.abbreviation] = item.content;
        });
        setBibliographyAbbreviations(abbrMap);
      },
      t('common.errors.loadingBibliography')
    );
  };

  const fetchHierarchicalUnit = async (unitId: string): Promise<void> => {
    await handleError(
      async () => {
        const unit = await organizationalUnitsService.getHierarchicalOrganizationalUnit(unitId);
        setHierarchicalUnit(unit);
      },
      t('common.errors.fetchingOrgUnits')
    );
  };

  const handleBackToLocations = () => {
    navigate('/locations');
  };

  const handleThumbnailClick = (selectedImage: Image) => {
    setImage(selectedImage);
  };

  const handleArtisticObjectClick = (artisticObject: ArtisticObjectListing) => {
    navigate(`/gallery/artistic-object/${artisticObject.id}`);
  };

  const handlePlanLocationClick = (locationCode: string, matchingObjects: ArtisticObjectListing[]) => {
    if (matchingObjects.length > 0) {
      navigate(`/gallery/artistic-object/${matchingObjects[0].id}`);
    }
  };

  const handleEditClick = () => {
    if (place?.id) {
      navigate(`/places/${place.id}/edit`);
    }
  };

  const canEdit = user?.role === UserRole.ADMIN || user?.role === UserRole.REDACTOR;

  // Get all non-plan images except the currently displayed one
  const thumbnailImages = place?.images?.filter(img => img.id !== image?.id && img.imageType !== ImageType.PLAN) || [];

  // Get plan images from the place
  const planImages = place?.images?.filter(img => img.imageType === ImageType.PLAN) || [];

  const buildBreadcrumbItems = (): BreadcrumbItem[] => {
    if (!hierarchicalUnit || !place) {
      return [];
    }

    const items: BreadcrumbItem[] = [];

    hierarchicalUnit.namesPath.forEach((name, index) => {
      const ouIds = hierarchicalUnit.idsPath.slice(0, index + 1);
      const queryParams = ouIds.map(id => `ou[]=${id}`).join('&');

      items.push({
        label: name,
        link: `/locations?${queryParams}`
      });
    });

    return items;
  };

  const breadcrumbItems = buildBreadcrumbItems();
  const lastBreadcrumb = breadcrumbItems[breadcrumbItems.length - 1];

  // Prepare key-value pairs for the info card
  const keyValuePairs = [
    {
      label: t('details.labels.churchUnit'),
      value: place?.churchUnit?.name,
      isClickable: !!lastBreadcrumb,
      to: lastBreadcrumb?.link
    },
    {
      label: t('details.labels.address'),
      value: formatAddress(place?.address)
    },
    // {
    //   label: t('details.labels.publication'),
    //   value: place?.publication?.name
    // }
  ];

  return (
    <div className="gallery-details bg-gray-100 py-8">
      <DetailsLayout
        onBackClick={handleBackToLocations}
        backButtonText={t('details.actions.backToLocations')}
      >

        <div className="lg:col-span-2 space-y-8">
          <DetailsImageSection
            mainImage={image}
            thumbnailImages={thumbnailImages}
            onThumbnailClick={handleThumbnailClick}
            altTextPrefix={placeName || t('details.labels.place')}
            placeholderAlt={placeName}
          >
            {breadcrumbItems.length > 0 && (
              <Breadcrumbs items={breadcrumbItems}/>
            )}
          </DetailsImageSection>

          {place?.sections && place.sections.length > 0 && (
            <div id="detailed-info" className="space-y-8">
              {sortPlaceSections(filterSectionsByLanguage(place.sections, i18n.language)).map((section) => (
                <DetailsDescriptionSection
                  key={section.id}
                  title={section.type.value}
                  content={section.content}
                  archiveAbbreviations={archiveAbbreviations}
                  bibliographyAbbreviations={bibliographyAbbreviations}
                  footnoteReferences={(section.type.alias !== 'FTNT' && section.type.alias !== 'PPLK') ? footnoteReferences : {}}
                />
              ))}
            </div>
          )}
        </div>

        <div id="info-section" className="space-y-6">
          <DetailsInfoCard
            title={placeName || t('details.loading')}
            keyValuePairs={keyValuePairs}
            onEditClick={canEdit ? handleEditClick : undefined}
          />

          <DetailsMapSection
            latitude={place?.address?.latitude}
            longitude={place?.address?.longitude}
            placeName={placeName}
          />

          <DetailsPlanSection
            planImages={planImages}
            entityName={placeName}
            artisticObjects={artisticObjects}
            onLocationClick={handlePlanLocationClick}
            placeId={place?.id}
          />

          <DetailsArtisticObjectsList
            artisticObjects={artisticObjects}
            onArtisticObjectClick={handleArtisticObjectClick}
            getLink={(obj) => `/gallery/artistic-object/${obj.id}`}
            title={t('details.sections.artisticObjectsInPlace')}
          />
        </div>
      </DetailsLayout>
    </div>
  );
};

export default PlaceDetails;
